import { stringifyQuery } from "@/utils";
import { Tag } from "@/types/Tag";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Trans, useLingui } from "@lingui/react/macro";
import { DialogProps } from "@radix-ui/react-dialog";
import { ROUTES } from "@/routes_new";
import { useMemo } from "react";
import { generatePath } from "react-router-dom";
import { Button } from "@components/ui/button";
import { MessageSquareIcon } from "lucide-react";
import { postTrackingData } from "@/api/endpoints/track";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemMedia,
  ItemTitle,
} from "@components/ui/item";
import { EVENT } from "@/tracking_events";
import { SearchFilterV4 } from "@/types/SearchFilter";

export default function TagExportDialog({
  isFavoritePage,
  filters,
  orgUuid,
  tag,
  ...rest
}: {
  isFavoritePage: boolean;
  filters: SearchFilterV4;
  orgUuid: Uuid;
  tag: Tag | undefined;
} & DialogProps) {
  const { t } = useLingui();

  // Helpers
  const tagName = isFavoritePage ? t`sparade omnämnanden` : tag?.name;

  const stringifiedFilters = useMemo(
    () => stringifyQuery({ ...filters, tag: tag?.slug }),
    [filters, tag?.slug]
  );

  // The org has to be in the path: these are plain link navigations, so the
  // AE-Organization header the API layer sends isn't available here.
  const excelUrl = `${generatePath(ROUTES.TAG_EXPORT_EXCEL_ORGID, {
    orgUuid,
  })}?${stringifiedFilters}`;
  const csvUrl = `${generatePath(ROUTES.TAG_EXPORT_CSV_ORGID, {
    orgUuid,
  })}?${stringifiedFilters}`;

  return (
    <Dialog {...rest}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Exportera {tagName}</Trans>
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans>
              Exportera alla omnämnanden för <q>{tagName}</q>
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <Item variant="outline" size="sm">
          <ItemMedia variant="icon">
            <MessageSquareIcon />
          </ItemMedia>
          <ItemContent>
            <ItemTitle>
              <Trans>Omnämnanden</Trans>
            </ItemTitle>
          </ItemContent>
          <ItemActions>
            {excelUrl && (
              <Button
                asChild
                size="sm"
                variant="outline"
                data-testid="excel-export-link"
                onClick={() => {
                  postTrackingData(EVENT.SHARE_TAG_CLICKED_EXCEL);
                }}
              >
                <a href={excelUrl}>
                  <Trans>Exportera till Excel</Trans>
                </a>
              </Button>
            )}

            {csvUrl && (
              <Button
                asChild
                size="sm"
                data-testid="csv-export-link"
                onClick={() => {
                  postTrackingData(EVENT.SHARE_TAG_CLICKED_CSV);
                }}
              >
                <a href={csvUrl}>
                  <Trans>Exportera till CSV</Trans>
                </a>
              </Button>
            )}
          </ItemActions>
        </Item>
      </DialogContent>
    </Dialog>
  );
}
